import React from "react";
import { themeGet } from "@styled-system/theme-get";
import { IconSVG, Span, SpanProps, useTheme } from "../..";
import { styled, EmotionComponent } from "../../styled";
import { RadioGroupContext } from "../RadioGroup/RadioGroupContext";
import { OverrideNativeElementProps } from "../../utils/styleFn";

export type RadioProps = OverrideNativeElementProps<
  {
    /** props to style the container */
    inputContainerProps?: SpanProps;
    /** size of the radio circle */
    iconSize?: string;
    /** title for radio icon*/
    title?: string;
  },
  "input"
>;

const OriginalRadioInput: EmotionComponent<
  React.HTMLProps<HTMLInputElement>
> = styled("input")`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  z-index: 1;
  /** here focus-visible and its polyfill seems works not consistent, use focus instead */
  &:focus + svg {
    outline: none;
    circle:first-of-type {
      stroke: ${themeGet("colors.secondaryDark")};
    }
  }

  &:checked + svg {
    circle:first-of-type {
      stroke: ${themeGet("colors.secondary")};
    }
    circle:last-of-type {
      display: block;
    }
  }
  &:hover + svg circle:first-of-type {
    stroke: ${themeGet("colors.secondaryDark")};
  }

  &:disabled + svg {
    circle:first-of-type {
      stroke-width: 0;
      fill: ${themeGet("colors.ui.borderColor")};
    }
  }
`;

type RadioIconProps = {
  iconSize?: string;
  title?: string;
};

const RadioIcon: React.FC<RadioIconProps> = ({ iconSize, title }) => {
  const theme = useTheme();
  return (
    <IconSVG
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      width={iconSize}
      height={iconSize}
    >
      {title && <title>{title}</title>}
      <circle
        stroke={theme?.colors?.["ui"]?.["borderColor"]}
        strokeWidth="2"
        fill="white"
        cx="10"
        cy="10"
        r="8"
      />
      <circle
        display="none"
        fill={theme?.colors?.["secondary"]}
        fillRule="nonzero"
        cx="10"
        cy="10"
        r="4"
      />
    </IconSVG>
  );
};

export const Radio: React.FC<RadioProps> = ({
  inputContainerProps,
  iconSize,
  checked: checkedProp,
  name: nameProp,
  onChange: onChangeProp,
  value: valueProp,
  required,
  disabled,
  title,
  ...otherProps
}) => {
  let checked = checkedProp;
  let name = nameProp;
  const radioGroupContext = React.useContext(RadioGroupContext);

  if (radioGroupContext) {
    if (typeof checked === "undefined") {
      checked = radioGroupContext.value === `${valueProp}`;
    }
    if (typeof name === "undefined") {
      name = radioGroupContext.name;
    }
  }

  const onChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (onChangeProp) {
      onChangeProp(e);
    }
    if (typeof checkedProp === "undefined" && radioGroupContext?.onChange) {
      radioGroupContext.onChange(e);
    }
  };

  return (
    <Span position="relative" {...inputContainerProps}>
      <OriginalRadioInput
        type="radio"
        checked={checked}
        name={name}
        value={valueProp}
        onChange={onChangeHandler}
        aria-checked={checked}
        aria-required={required}
        aria-disabled={disabled}
        required={required}
        disabled={disabled}
        {...otherProps}
      />
      <RadioIcon iconSize={iconSize} title={title} />
    </Span>
  );
};

Radio.displayName = "Radio";

export default Radio;
